import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export type DashboardData = {
  totals: {
    net_revenue: number;
    total_sales: number;
    orders_count: number;
    items_sold: number;
    refunds: number;
    total_customers: number;
    avg_order_value: number;
  };
  prev_totals: {
    net_revenue: number;
    total_sales: number;
    orders_count: number;
    items_sold: number;
    refunds: number;
    total_customers: number;
    avg_order_value: number;
  };
  intervals: {
    interval: string;
    date_start: string;
    date_end: string;
    subtotals: {
      net_revenue: number;
      orders_count: number;
    };
  }[];
  top_products: {
    product_id: number;
    items_sold: number;
    net_revenue: number;
    name: string;
  }[];
};

type AggregatedTotals = {
  net_revenue: number;
  orders_count: number;
  num_items_sold: number;
  total_customers: number;
  refunds: number;
};

export type GodViewData = {
  aggregated: AggregatedTotals;
  prevAggregated: AggregatedTotals;
  perStore: {
    storeId: string;
    storeName: string;
    net_revenue: number;
    orders_count: number;
    num_items_sold: number;
    total_customers: number;
  }[];
};

export function useGodView(after: string, before: string, enabled: boolean) {
  const params = new URLSearchParams({ after, before });
  return useQuery({
    queryKey: ["godView", after, before],
    queryFn: () => fetchJson<GodViewData>(`/api/analytics/god-view?${params}`),
    enabled: enabled && !!after && !!before,
    staleTime: 2 * 60 * 1000,
  });
}

export type RevenueAnalytics = {
  totals: { net_revenue: number; orders_count: number; refunds: number };
  prev_totals: { net_revenue: number; orders_count: number; refunds: number };
  by_payment: { method: string; title: string; revenue: number; orders: number }[];
  by_shipping: { method: string; title: string; revenue: number; orders: number }[];
  by_category: { id: number; name: string; revenue: number; orders: number; items_sold: number }[];
  by_coupon: { code: string; orders: number; discount_total: number }[];
};

export type CustomerAnalytics = {
  summary: {
    total_customers: number;
    new_customers: number;
    returning_customers: number;
    repeat_rate: number;
    avg_lifetime_value: number;
    avg_days_between_orders: number;
  };
  prev_summary: { total_customers: number };
  top_customers: {
    id: number;
    name: string;
    email: string;
    orders_count: number;
    total_spent: number;
    last_order: string;
  }[];
};

export type GeoAnalytics = {
  by_city: { city: string; revenue: number; orders: number }[];
};

export type ProductAnalytics = {
  top_sellers: { id: number; name: string; items_sold: number; revenue: number; image: string | null }[];
  slow_movers: { id: number; name: string; stock: number; days_since_last_sale: number }[];
  frequently_together: { product_a_id: number; product_a_name: string; product_b_id: number; product_b_name: string; times_together: number }[];
  stock_velocity: { id: number; name: string; avg_sold_per_week: number; stock: number; weeks_until_out: number }[];
};

export function useRevenueAnalytics(storeId: string | null, after: string, before: string, enabled: boolean) {
  const params = new URLSearchParams({ storeId: storeId || "", after, before });
  return useQuery({
    queryKey: ["analytics", "revenue", storeId, after, before],
    queryFn: () => fetchJson<RevenueAnalytics>(`/api/analytics/revenue?${params}`),
    enabled: enabled && !!storeId && !!after && !!before,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCustomerAnalytics(storeId: string | null, after: string, before: string, enabled: boolean) {
  const params = new URLSearchParams({ storeId: storeId || "", after, before });
  return useQuery({
    queryKey: ["analytics", "customers", storeId, after, before],
    queryFn: () => fetchJson<CustomerAnalytics>(`/api/analytics/customers?${params}`),
    enabled: enabled && !!storeId && !!after && !!before,
    staleTime: 2 * 60 * 1000,
  });
}

export function useGeoAnalytics(storeId: string | null, after: string, before: string, enabled: boolean) {
  const params = new URLSearchParams({ storeId: storeId || "", after, before });
  return useQuery({
    queryKey: ["analytics", "geo", storeId, after, before],
    queryFn: () => fetchJson<GeoAnalytics>(`/api/analytics/geo?${params}`),
    enabled: enabled && !!storeId && !!after && !!before,
    staleTime: 2 * 60 * 1000,
  });
}

export function useProductAnalytics(storeId: string | null, after: string, before: string, enabled: boolean) {
  const params = new URLSearchParams({ storeId: storeId || "", after, before });
  return useQuery({
    queryKey: ["analytics", "products", storeId, after, before],
    queryFn: () => fetchJson<ProductAnalytics>(`/api/analytics/products?${params}`),
    enabled: enabled && !!storeId && !!after && !!before,
    staleTime: 2 * 60 * 1000,
  });
}

export type AbcClass = "A" | "B" | "C";

export interface AbcVariation {
  variationId: number;
  sku: string | null;
  name: string | null;
  quantity: number;
  revenue: number;
  stock: number | null;
}

export interface AbcProduct {
  productId: number;
  name: string;
  sku: string | null;
  imageUrl: string | null;
  abcClass: AbcClass;
  revenue: number;
  quantity: number;
  cumulativeShare: number;
  stock: number | null;
  stockValue: number;
  dailySales: number;
  coverageDays: number | null;
  dateCreated: string | null;
  noSales: boolean;
  variations: AbcVariation[];
}

export interface AbcClassSummary {
  abcClass: AbcClass;
  productCount: number;
  revenue: number;
  revenueShare: number;
  stockValue: number;
  stockShare: number;
}

export interface AbcAnalysis {
  summary: {
    orderNow: { count: number; items: AbcProduct[] };
    trappedCapital: { count: number; totalValue: number; items: AbcProduct[] };
  };
  classes: AbcClassSummary[];
  pareto: { productId: number; name: string; value: number; cumulativeShare: number; abcClass: AbcClass }[];
  products: AbcProduct[];
  periodDays: number;
}

export function isOrderNow(p: AbcProduct): boolean {
  return p.abcClass === "A" && p.stock !== null && p.coverageDays !== null && p.coverageDays < 14;
}

export function isTrappedCapital(p: AbcProduct): boolean {
  return (
    p.abcClass === "C" &&
    p.stock !== null &&
    p.stock > 0 &&
    (p.noSales || (p.coverageDays !== null && p.coverageDays > 90))
  );
}

// Sume po klasama iz proizvoljnog skupa proizvoda. Server šalje `classes` za PUNI
// skup, pa se s aktivnim filterom („Sakrij C mlađe od N dana") struktura asortimana
// mora preračunati iz vidljivih proizvoda — inače broj proizvoda dolazi iz filtriranog,
// a KM i procenti iz nefiltriranog skupa. Zaokruživanje i zaštita od dijeljenja nulom
// su namjerno iste kao u LocalAnalyticsRepository.abc.
const round2 = (n: number) => Math.round(n * 100) / 100;

export function abcClassSummaries(products: AbcProduct[]): AbcClassSummary[] {
  const totalRevenue = products.reduce((s, p) => s + p.revenue, 0);
  const totalStockValue = products.reduce((s, p) => s + p.stockValue, 0);
  return (["A", "B", "C"] as AbcClass[]).map((abcClass) => {
    const list = products.filter((p) => p.abcClass === abcClass);
    const revenue = round2(list.reduce((s, p) => s + p.revenue, 0));
    const stockValue = round2(list.reduce((s, p) => s + p.stockValue, 0));
    return {
      abcClass,
      productCount: list.length,
      revenue,
      revenueShare: totalRevenue > 0 ? round2((revenue / totalRevenue) * 100) : 0,
      stockValue,
      stockShare: totalStockValue > 0 ? round2((stockValue / totalStockValue) * 100) : 0,
    };
  });
}

export type AbcErrorCode = "not_webhook" | "backfill_running" | undefined;

async function fetchAbc(url: string): Promise<AbcAnalysis> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const e: any = new Error(body.error || res.statusText);
    e.code = body.code;
    throw e;
  }
  return res.json();
}

export function useAbcAnalytics(
  storeId: string | null,
  after: string,
  before: string,
  metric: "revenue" | "quantity",
  enabled: boolean
) {
  const params = new URLSearchParams({ storeId: storeId ?? "", after, before, metric });
  return useQuery({
    queryKey: ["analytics", "abc", storeId, after, before, metric],
    queryFn: () => fetchAbc(`/api/analytics/abc?${params}`),
    enabled: enabled && !!storeId && !!after && !!before,
    retry: (failureCount, error: any) => error?.code === undefined && failureCount < 2,
  });
}

export function useDashboard(
  storeId: string | null,
  after: string,
  before: string,
  interval: string
) {
  const params = new URLSearchParams({
    storeId: storeId || "",
    after,
    before,
    interval,
  });
  return useQuery({
    queryKey: ["dashboard", storeId, after, before, interval],
    queryFn: () => fetchJson<DashboardData>(`/api/analytics/dashboard?${params}`),
    enabled: !!storeId && !!after && !!before,
    staleTime: 2 * 60 * 1000,
  });
}
